(() => {
  const doc = document;
  const body = doc.body;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const effectsEnabled = body.dataset.animations !== "false" && !prefersReducedMotion.matches;
  const parallaxEnabled = effectsEnabled && body.dataset.parallax !== "false";
  const tiltEnabled = effectsEnabled && body.dataset.cardTilt !== "false";
  const pointerFine = window.matchMedia("(pointer: fine)").matches;

  const qs = (selector, scope = doc) => scope.querySelector(selector);
  const qsa = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));
  const themeRoot = doc.documentElement;
  const themeStorageKey = "daily-essentials-theme";

  const escapeHTML = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents);
    }

    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency
    }).format((cents || 0) / 100);
  };

  const setButtonState = (button, state) => {
    if (!button) return;
    const label = button.querySelector("span");
    if (!button.dataset.originalLabel && label) {
      button.dataset.originalLabel = label.textContent;
    }

    button.classList.toggle("is-loading", state === "loading");
    button.classList.toggle("is-success", state === "success");
    button.disabled = state === "loading";

    if (label) {
      if (state === "loading") label.textContent = "Adding...";
      if (state === "success") label.textContent = "Added";
      if (state === "idle") label.textContent = button.dataset.originalLabel || label.textContent;
    }
  };

  const setThemeMode = (theme, persist = true) => {
    const nextTheme = theme === "dark" ? "dark" : "light";
    const themeColor = qs("#ThemeColor");

    themeRoot.dataset.theme = nextTheme;
    themeRoot.style.colorScheme = nextTheme;
    body.dataset.theme = nextTheme;

    if (themeColor) {
      themeColor.setAttribute(
        "content",
        nextTheme === "dark"
          ? themeColor.dataset.darkColor || "#080808"
          : themeColor.dataset.lightColor || "#F5F7FB"
      );
    }

    qsa("[data-theme-toggle]").forEach((button) => {
      const isDark = nextTheme === "dark";
      button.setAttribute("aria-pressed", String(isDark));
      button.setAttribute("aria-label", isDark ? "Switch to light mode" : "Switch to dark mode");
    });

    if (persist) {
      try {
        localStorage.setItem(themeStorageKey, nextTheme);
      } catch (error) {
        console.warn(error);
      }
    }
  };

  const resolveThemeMode = (mode) => {
    if (mode === "dark" || mode === "light") return mode;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  };

  const initThemeMode = () => {
    if (body.dataset.darkMode !== "true") {
      setThemeMode("light", false);
      return;
    }

    let storedTheme = null;
    try {
      storedTheme = localStorage.getItem(themeStorageKey);
    } catch (error) {
      storedTheme = null;
    }

    const defaultMode = body.dataset.defaultColorMode || "system";
    const systemQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setThemeMode(resolveThemeMode(storedTheme || defaultMode), false);

    qsa("[data-theme-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        setThemeMode(themeRoot.dataset.theme === "dark" ? "light" : "dark");
      });
    });

    const syncSystemTheme = () => {
      try {
        if (!localStorage.getItem(themeStorageKey) && defaultMode === "system") {
          setThemeMode(resolveThemeMode("system"), false);
        }
      } catch (error) {
        setThemeMode(resolveThemeMode("system"), false);
      }
    };

    if (typeof systemQuery.addEventListener === "function") {
      systemQuery.addEventListener("change", syncSystemTheme);
    } else if (typeof systemQuery.addListener === "function") {
      systemQuery.addListener(syncSystemTheme);
    }
  };

  const initHeader = () => {
    const header = qs("[data-site-header]");
    const progress = qs("[data-scroll-progress]");
    let ticking = false;

    const update = () => {
      const scrollTop = window.scrollY || doc.documentElement.scrollTop;
      const height = doc.documentElement.scrollHeight - window.innerHeight;
      if (header) header.classList.toggle("is-scrolled", scrollTop > 10);
      if (progress) progress.style.width = `${height > 0 ? (scrollTop / height) * 100 : 0}%`;
      ticking = false;
    };

    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  };

  const initReveals = () => {
    const items = qsa(".reveal-on-scroll, .reveal-line");
    if (!items.length) return;

    if (!effectsEnabled || !("IntersectionObserver" in window)) {
      items.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -8% 0px" }
    );

    items.forEach((item) => observer.observe(item));
  };

  const initMouseGlow = () => {
    const glow = qs("[data-cursor-glow]");
    if (!glow || body.dataset.mouseGlow === "false" || !effectsEnabled || !pointerFine) return;

    let x = 0;
    let y = 0;
    let frame = null;
    body.classList.add("has-cursor-glow");

    const render = () => {
      glow.style.left = `${x}px`;
      glow.style.top = `${y}px`;
      frame = null;
    };

    window.addEventListener(
      "pointermove",
      (event) => {
        x = event.clientX;
        y = event.clientY;
        if (!frame) frame = window.requestAnimationFrame(render);
      },
      { passive: true }
    );
  };

  const initHeroPointer = () => {
    const hero = qs("[data-hero-motion]");
    const product = qs("[data-float-product]", hero || doc);
    if (!hero || !product || !effectsEnabled || !pointerFine) return;

    hero.addEventListener(
      "pointermove",
      (event) => {
        const rect = hero.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        product.style.setProperty("--hero-x", `${x * 14}px`);
        product.style.setProperty("--hero-y", `${y * 12}px`);
        product.style.transform = `translate3d(${x * 14}px, ${y * 12}px, 0) rotateY(${x * 5}deg) rotateX(${-y * 4}deg)`;
      },
      { passive: true }
    );

    hero.addEventListener("pointerleave", () => {
      product.style.transform = "";
    });
  };

  const initParallax = () => {
    const layers = qsa("[data-parallax-layer]");
    if (!layers.length || !parallaxEnabled) return;

    let ticking = false;
    const update = () => {
      const viewportMid = window.innerHeight / 2;
      layers.forEach((layer) => {
        const rect = layer.getBoundingClientRect();
        const distance = viewportMid - (rect.top + rect.height / 2);
        const offset = Math.max(Math.min(distance * 0.025, 18), -18);
        layer.style.translate = `0 ${offset}px`;
      });
      ticking = false;
    };

    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  };

  const initTiltCards = () => {
    if (!tiltEnabled || !pointerFine) return;

    qsa("[data-tilt-card]").forEach((card) => {
      card.addEventListener(
        "pointermove",
        (event) => {
          const rect = card.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          card.style.transform = `perspective(900px) rotateX(${-y * 5}deg) rotateY(${x * 6}deg) translateY(-3px)`;
        },
        { passive: true }
      );

      card.addEventListener("pointerleave", () => {
        card.style.transform = "";
      });
    });
  };

  const initMagneticButtons = () => {
    if (!effectsEnabled || !pointerFine) return;

    qsa(".magnetic-button").forEach((button) => {
      button.addEventListener(
        "pointermove",
        (event) => {
          const rect = button.getBoundingClientRect();
          const x = (event.clientX - rect.left - rect.width / 2) * 0.12;
          const y = (event.clientY - rect.top - rect.height / 2) * 0.16;
          button.style.translate = `${x}px ${y}px`;
        },
        { passive: true }
      );

      button.addEventListener("pointerleave", () => {
        button.style.translate = "";
      });
    });
  };

  const initMobileMenu = () => {
    const toggle = qs("[data-menu-toggle]");
    const menu = qs("[data-mobile-menu]");
    if (!toggle || !menu) return;

    const close = () => {
      toggle.setAttribute("aria-expanded", "false");
      menu.classList.remove("is-open");
      body.classList.remove("menu-open");
      window.setTimeout(() => {
        if (!menu.classList.contains("is-open")) menu.hidden = true;
      }, 180);
    };

    toggle.addEventListener("click", () => {
      const open = toggle.getAttribute("aria-expanded") !== "true";
      toggle.setAttribute("aria-expanded", String(open));
      menu.hidden = false;
      menu.classList.toggle("is-open", open);
      body.classList.toggle("menu-open", open);
    });

    qsa("a", menu).forEach((link) => link.addEventListener("click", close));
    doc.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });
  };

  const initAccordions = () => {
    qsa("[data-accordion-button]").forEach((button) => {
      const panel = doc.getElementById(button.getAttribute("aria-controls"));
      if (!panel) return;

      button.addEventListener("click", () => {
        const expanded = button.getAttribute("aria-expanded") === "true";
        button.setAttribute("aria-expanded", String(!expanded));
        panel.hidden = expanded;
      });
    });
  };

  const initCarousel = () => {
    qsa("[data-testimonial-carousel]").forEach((carousel) => {
      const track = qs("[data-carousel-track]", carousel);
      const slides = qsa("[data-carousel-slide]", carousel);
      const prev = qs("[data-carousel-prev]", carousel);
      const next = qs("[data-carousel-next]", carousel);
      if (!track || slides.length < 2) return;

      let index = 0;

      const maxIndex = () => Math.max(0, slides.length - (window.innerWidth < 720 ? 1 : 3));
      const update = () => {
        const slideRect = slides[0].getBoundingClientRect();
        const gap = parseFloat(window.getComputedStyle(track).gap) || 0;
        index = Math.max(0, Math.min(index, maxIndex()));
        track.style.transform = `translate3d(${-index * (slideRect.width + gap)}px, 0, 0)`;
      };

      prev && prev.addEventListener("click", () => {
        index -= 1;
        update();
      });

      next && next.addEventListener("click", () => {
        index += 1;
        update();
      });

      window.addEventListener("resize", update, { passive: true });
      update();
    });
  };

  const initProductGallery = () => {
    qsa("[data-product-gallery]").forEach((gallery) => {
      const media = qsa("[data-product-main-media]", gallery);
      const thumbs = qsa("[data-product-thumb]", gallery);

      const show = (index) => {
        const activeIndex = Number.isFinite(index) ? index : 0;
        media.forEach((item, itemIndex) => item.classList.toggle("is-active", itemIndex === activeIndex));
        thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle("is-active", thumbIndex === activeIndex));

        const activeThumb = thumbs[activeIndex];
        if (activeThumb && typeof activeThumb.scrollIntoView === "function") {
          activeThumb.scrollIntoView({ behavior: effectsEnabled ? "smooth" : "auto", block: "nearest", inline: "nearest" });
        }
      };

      gallery.showProductMedia = show;
      gallery.showProductMediaById = (mediaId, imageUrl) => {
        let mediaIndex = -1;
        if (mediaId) {
          mediaIndex = media.findIndex((item) => item.dataset.mediaId === String(mediaId));
        }
        if (mediaIndex < 0 && imageUrl) {
          mediaIndex = media.findIndex((item) => item.dataset.mediaImage === imageUrl);
        }
        if (mediaIndex >= 0) show(mediaIndex);
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener("click", () => show(Number(thumb.dataset.productThumb || 0)));
      });
    });
  };

  const initProductForms = () => {
    qsa("[data-variant-select]").forEach((select) => {
      const updateVariant = () => {
        const selected = select.selectedOptions[0];
        const section = select.closest("[data-product-section]");
        const price = qs("[data-product-price] .price__current", section || doc);
        const submit = qs(".product-form__submit", section || doc);
        const submitLabel = submit ? qs("span", submit) : null;
        const stickyPrice = qs("[data-sticky-product-price]", section || doc);
        const stickyVariant = qs("[data-sticky-product-variant]", section || doc);
        const gallery = qs("[data-product-gallery]", section || doc);
        const preview = qs("[data-selected-variant-preview]", section || doc);
        const previewImage = preview ? qs("[data-selected-variant-image]", preview) : null;
        const previewMedia = preview ? qs(".selected-variant-preview__media", preview) : null;
        const previewTitle = preview ? qs("[data-selected-variant-title]", preview) : null;
        const variantButtons = qsa("[data-variant-option]", section || doc);

        if (price && selected && selected.dataset.price) price.textContent = selected.dataset.price;
        if (stickyPrice && selected && selected.dataset.price) {
          const price = selected.dataset.price;
          const comparePrice = selected.dataset.comparePrice;
        if (comparePrice && comparePrice !== price) {
          stickyPrice.innerHTML = `<strong>${price}</strong> <span style="text-decoration:line-through;color:#999;font-size:13px;">${comparePrice}</span> <span style="background:#E00000;color:#fff;font-size:11px;font-weight:bold;padding:3px 7px;border-radius:4px;">40% Discount</span>`;
        } else {
            stickyPrice.textContent = price;
          }
        }

        if (stickyVariant && selected) stickyVariant.textContent = selected.dataset.variantTitle || selected.textContent.trim();

        if (submit && selected) {
          const available = selected.dataset.available !== "false" && !selected.disabled;
          submit.disabled = !available;
          if (submitLabel) {
            submitLabel.textContent = available
              ? submit.dataset.addToCartLabel || submitLabel.textContent
              : submit.dataset.soldOutLabel || submitLabel.textContent;
          }
        }

        if (gallery && selected && typeof gallery.showProductMediaById === "function") {
          gallery.showProductMediaById(selected.dataset.mediaId, selected.dataset.image);
        }

        if (preview && selected) {
          if (previewTitle) previewTitle.textContent = selected.dataset.variantTitle || selected.textContent.trim();
          if (previewImage && selected.dataset.image) {
            previewImage.src = selected.dataset.image;
            previewImage.alt = selected.dataset.variantTitle || selected.textContent.trim();
            preview.hidden = false;
            if (previewMedia) previewMedia.classList.remove("is-empty");
          }
        }

        variantButtons.forEach((button) => {
          const active = selected && button.dataset.variantOption === selected.value;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-selected", String(active));
        });
      };

      qsa("[data-variant-option]", select.closest("[data-product-section]") || doc).forEach((button) => {
        button.addEventListener("click", () => {
          if (button.disabled) return;
          select.value = button.dataset.variantOption || select.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });

      select.addEventListener("change", updateVariant);
      updateVariant();
    });

    qsa("[data-quantity]").forEach((stepper) => {
      const input = qs("input", stepper);
      const display = qs("[data-cart-quantity-display]", stepper);
      const minus = qs("[data-quantity-minus]", stepper);
      const plus = qs("[data-quantity-plus]", stepper);

      if (!input) return;

      minus &&
        minus.addEventListener("click", () => {
          input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });

      plus &&
        plus.addEventListener("click", () => {
          input.value = Number(input.value || 1) + 1;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });

      input.addEventListener("change", () => {
        if (display) display.textContent = input.value;
      });
    });

    qsa("[data-submit-product]").forEach((button) => {
      button.addEventListener("click", () => {
        const submit = qs(".product-form__submit");
        if (submit) submit.click();
      });
    });
  };

  const initCollection = () => {
    qsa("[data-sort-select]").forEach((select) => {
      select.addEventListener("change", () => select.form && select.form.submit());
    });

    const openButton = qs("[data-filter-open]");
    const shell = qs("[data-filter-shell]");
    const drawer = qs("[data-filter-drawer]");
    const overlay = qs(".filter-drawer__overlay");
    if (!openButton || !shell || !drawer || !overlay) return;

    const open = () => {
      overlay.hidden = false;
      drawer.setAttribute("aria-hidden", "false");
      body.classList.add("filter-open");
      window.setTimeout(() => drawer.focus(), 40);
    };

    const close = () => {
      body.classList.remove("filter-open");
      drawer.setAttribute("aria-hidden", "true");
      window.setTimeout(() => {
        if (!body.classList.contains("filter-open")) overlay.hidden = true;
      }, 240);
    };

    openButton.addEventListener("click", open);
    qsa("[data-filter-close]", shell).forEach((button) => button.addEventListener("click", close));
    doc.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });
  };

  const renderCartItems = (cart) => {
    const container = qs("[data-cart-items]");
    const subtotal = qs("[data-cart-subtotal]");
    const counts = qsa("[data-cart-count]");
    if (!container) return;

    counts.forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.add("is-bumping");
      window.setTimeout(() => count.classList.remove("is-bumping"), 430);
    });

    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    if (!cart.items.length) {
      container.innerHTML = `<p class="cart-empty">Your cart is empty</p>`;
      return;
    }

    container.innerHTML = cart.items
      .map((item) => {
        const image = item.image
          ? `<img src="${item.image}" alt="${escapeHTML(item.product_title)}" loading="lazy" width="90" height="90">`
          : `<span class="cart-item__placeholder" aria-hidden="true"></span>`;
        const variant =
          item.variant_title && item.variant_title !== "Default Title"
            ? `<p class="cart-item__variant">${escapeHTML(item.variant_title)}</p>`
            : "";

        return `
          <div class="cart-item" data-cart-item data-key="${escapeHTML(item.key)}">
            <a class="cart-item__media" href="${item.url}">${image}</a>
            <div class="cart-item__details">
              <a class="cart-item__title" href="${item.url}">${escapeHTML(item.product_title)}</a>
              ${variant}
              <div class="cart-item__meta">
                <div class="quantity-stepper">
                  <button type="button" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity-value="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">-</button>
                  <span data-cart-quantity-display>${item.quantity}</span>
                  <button type="button" data-cart-quantity data-key="${escapeHTML(item.key)}" data-quantity-value="${item.quantity + 1}" aria-label="Increase quantity">+</button>
                </div>
                <button class="cart-item__remove" type="button" data-cart-remove data-key="${escapeHTML(item.key)}">Remove</button>
              </div>
            </div>
            <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
          </div>
        `;
      })
      .join("");
  };

  const fetchCart = async () => {
    const response = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : "/"}cart.js`, {
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error("Could not load cart");
    return response.json();
  };

  const updateCart = async () => {
    const cart = await fetchCart();
    renderCartItems(cart);
    return cart;
  };

  const changeCartItem = async (key, quantity) => {
    const response = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : "/"}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error("Could not update cart");
    const cart = await response.json();
    renderCartItems(cart);
  };

  const initCartDrawer = () => {
    const drawer = qs("[data-cart-drawer]");
    const shell = qs("[data-cart-shell]");
    const overlay = qs(".cart-drawer__overlay");
    const openButtons = qsa("[data-cart-open]");
    if (!drawer || !shell || !overlay) return;

    const open = async () => {
      overlay.hidden = false;
      drawer.setAttribute("aria-hidden", "false");
      body.classList.add("drawer-open");
      try {
        await updateCart();
      } catch (error) {
        console.warn(error);
      }
      window.setTimeout(() => drawer.focus(), 40);
    };

    const close = () => {
      body.classList.remove("drawer-open");
      drawer.setAttribute("aria-hidden", "true");
      window.setTimeout(() => {
        if (!body.classList.contains("drawer-open")) overlay.hidden = true;
      }, 260);
    };

    openButtons.forEach((button) => button.addEventListener("click", open));
    qsa("[data-cart-close]", shell).forEach((button) => button.addEventListener("click", close));

    shell.addEventListener("click", async (event) => {
      const remove = event.target.closest("[data-cart-remove]");
      const quantity = event.target.closest("[data-cart-quantity]");

      if (remove) {
        const item = remove.closest("[data-cart-item]");
        item && item.classList.add("is-removing");
        await changeCartItem(remove.dataset.key, 0);
      }

      if (quantity) {
        await changeCartItem(quantity.dataset.key, Number(quantity.dataset.quantityValue || 0));
      }
    });

    doc.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });

    window.DailyEssentialsCart = { open, close, update: updateCart };
  };

  const initAjaxProductForms = () => {
    qsa("form[data-product-form]").forEach((form) => {
      form.addEventListener("submit", async (event) => {
        const drawerAvailable = Boolean(qs("[data-cart-drawer]"));
        if (!drawerAvailable) return;

        event.preventDefault();
        const button = event.submitter || qs("button[type='submit']", form);
        setButtonState(button, "loading");

        try {
          const response = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : "/"}cart/add.js`, {
            method: "POST",
            headers: { Accept: "application/json" },
            body: new FormData(form)
          });

          if (!response.ok) throw new Error("Could not add product");

          await updateCart();
          setButtonState(button, "success");
          if (window.DailyEssentialsCart) window.DailyEssentialsCart.open();
        } catch (error) {
          console.warn(error);
          setButtonState(button, "idle");
        } finally {
          window.setTimeout(() => setButtonState(button, "idle"), 1300);
        }
      });
    });
  };

  const init = () => {
    initThemeMode();
    initHeader();
    initReveals();
    initMouseGlow();
    initHeroPointer();
    initParallax();
    initTiltCards();
    initMagneticButtons();
    initMobileMenu();
    initAccordions();
    initCarousel();
    initProductGallery();
    initProductForms();
    initCollection();
    initCartDrawer();
    initAjaxProductForms();
  };

  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
